import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { scrollToId, EASE } from "../lib/util";
import { usePlaceholder } from "../lib/usePlaceholder";
import { Logo } from "./Logo";
import { LangSwitch } from "./LangSwitch";

const navLinks = (t) => [
  ["home", t.nav.home],
  ["services", t.nav.services],
  ["how", t.nav.how],
  ["about", t.nav.about],
];

const MobileMenu = ({ open, onClose }) => {
  const { t } = useLang();
  const consult = usePlaceholder("consultation");
  const signin = usePlaceholder("signin");
  const go = (id) => {
    onClose();
    setTimeout(() => scrollToId(id), 350);
  };
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="fixed inset-0 z-[70] flex flex-col bg-noor-ink px-6 pb-10 pt-6"
        >
          <div className="flex items-center justify-between">
            <Logo />
            <button data-testid="mobile-menu-close" aria-label={t.nav.close} onClick={onClose} className="grid h-12 w-12 place-items-center rounded-full border border-noor-pearl/15 text-noor-pearl">
              <X size={20} />
            </button>
          </div>
          <nav className="mt-16 flex flex-col gap-2">
            {navLinks(t).map(([id, label], i) => (
              <motion.button
                key={id}
                data-testid={`mobile-nav-${id}`}
                onClick={() => go(id)}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + i * 0.07, duration: 0.7, ease: EASE }}
                className="border-b border-noor-pearl/10 py-4 text-start font-display text-4xl text-noor-pearl"
              >
                {label}
              </motion.button>
            ))}
          </nav>
          <div className="mt-auto flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <LangSwitch idPrefix="mobile" />
              <a {...signin} data-testid="mobile-signin" className="text-sm text-noor-mist underline-offset-4 hover:underline">{t.nav.signin}</a>
            </div>
            <a {...consult} data-testid="mobile-menu-consult" className="btn-primary justify-center">
              <span className="relative z-10">{t.hero.cta1}</span>
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const consult = usePlaceholder("consultation");
  const signin = usePlaceholder("signin");

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 px-3 pt-3 lg:px-8 lg:pt-5">
        <nav
          data-testid="main-nav"
          className={`mx-auto flex h-16 max-w-[1440px] items-center justify-between rounded-full border px-3 transition-[background-color,border-color] duration-700 lg:px-5 ${scrolled ? "border-noor-champagne/15 bg-noor-deep/85 backdrop-blur-xl" : "border-transparent bg-transparent"}`}
        >
          <button data-testid="nav-logo" aria-label="NOORIN" onClick={() => scrollToId("home")} className="ps-2">
            <Logo />
          </button>
          <ul className="hidden items-center gap-9 lg:flex">
            {navLinks(t).map(([id, label]) => (
              <li key={id}>
                <button data-testid={`nav-${id}`} onClick={() => scrollToId(id)} className="nav-link text-[14px] text-noor-pearl/80 hover:text-noor-pearl">
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-2 lg:gap-3">
            <LangSwitch />
            <a {...signin} data-testid="nav-signin" className="hidden px-3 text-[14px] text-noor-pearl/80 transition-colors hover:text-noor-pearl md:inline">{t.nav.signin}</a>
            <a {...consult} data-testid="nav-consult" className="hidden h-10 items-center rounded-full bg-noor-champagne px-5 text-[13px] font-semibold text-noor-deep transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-noor-pearl sm:inline-flex">
              {t.nav.consult}
            </a>
            <button data-testid="nav-menu-toggle" aria-label={t.nav.menu} onClick={() => setOpen(true)} className="grid h-11 w-11 place-items-center rounded-full border border-noor-pearl/15 text-noor-pearl lg:hidden">
              <Menu size={19} />
            </button>
          </div>
        </nav>
      </header>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
};
